import { getDateFromString } from '@ourbranch/date-fns-helpers';
import { isBefore, addDays, subYears } from 'date-fns';
import { isFarmBasedPolicyType } from '@ourbranch/policy-types';

export const surplusContributionMultiplier = 0.02; // this is the default

// #34673: Branch farm launched 2026-04-01 (also the date farm surplus contribution turns on, below).
// A migrated policy's Branch inception can't precede this, so it is the floor for the surplus 5-year
// decay clock — legacy pre-carrier inception dates must not age a just-migrated farm policy.
const FARM_SURPLUS_INCEPTION_FLOOR = getDateFromString('2026-04-01');
export const surplusContributionMultiplierByDate = [
  {
    startDateAsInt: 20230624,
    endDateAsInt: 30000101,
    multiplier: 0.15
  }
]; // for changes over time

export function getSurplusContributionMultiplier(
  rateControlDate: string | null,
  state: string | null,
  policyInceptionDate: string | null | undefined,
  policyType: string | null = null
) {
  let rateControlDateAsInt = parseInt(String(rateControlDate).replace(/-/g, ''), 10);

  // Farm surplus contribution is 0 before 2026-04-01
  if (isFarmBasedPolicyType(policyType) && rateControlDateAsInt < 20260401) {
    return 0;
  }
  if (state === 'MI' && rateControlDateAsInt === 20230701) {
    // this case requires that we hand set the rate control date to the day before
    rateControlDateAsInt = 20230623;
  }

  // if the policy inception date is before ((rate control date plus 100 days) minus five years), use the default multiplier
  // we do rate control date plus 100 days to handle renewal time periods that can be that long
  // and to avoid needing to send in the policy effective date as well (also effective date moves more than rate control date)
  // to handle our rule that surplus contribution goes to the default (2%) after five years.
  // (this is all in Github issue 24724)
  if (policyInceptionDate && rateControlDate) {
    // using the date-fns library, calculate whether the policy inception date is
    // before ((rate control date plus 100 days) minus five years)
    let policyInceptionDateAsDate = getDateFromString(policyInceptionDate);
    // #34673: Migrated Forreston farm policies carry a LEGACY inception date from the prior carrier
    // (e.g. 2021), but they only entered the Branch system when farm launched (2026-04-01). The
    // 5-year decay must count from that Branch inception ("term 1" in our system — can't be before
    // 2026), not the legacy inception; otherwise a migrated policy immediately looks older than five
    // years and drops to the 2% default, while the declaration (which passes rateControlDate as the
    // inception) correctly shows 15%. Floor the farm inception at the farm-launch date so the decay
    // clock starts in 2026 (it will legitimately begin ~2031). Non-farm lines age normally.
    if (isFarmBasedPolicyType(policyType) && isBefore(policyInceptionDateAsDate, FARM_SURPLUS_INCEPTION_FLOOR)) {
      policyInceptionDateAsDate = FARM_SURPLUS_INCEPTION_FLOOR;
    }
    const rateControlDateAsDate = getDateFromString(rateControlDate);
    const rateControlDatePlus100DaysMinus5Years = subYears(addDays(rateControlDateAsDate, 100), 5);
    if (isBefore(policyInceptionDateAsDate, rateControlDatePlus100DaysMinus5Years)) {
      return surplusContributionMultiplier;
    }
  }
  for (const scm of surplusContributionMultiplierByDate) {
    if (rateControlDateAsInt >= scm.startDateAsInt && rateControlDateAsInt <= scm.endDateAsInt) {
      return scm.multiplier;
    }
  }

  return surplusContributionMultiplier;
} // getSurplusContributionMultiplier
